import { SuretyAccountId } from "@inrev/common";
import { useFetchAccountMetadata, useFetchSuretyAccount } from "@portal/api/account";
import { ReportedRetryableLoadError } from "@portal/components/features/load-errors";
import { RetryableLoadError } from "@portal/components/patterns/retryable-load-error";
import { Card, ClickableIcon, PartialIndicator, Text } from "@portal/components/primitives";
import { CenteredSpinner } from "@portal/components/ui/CenteredSpinner";
import { useShowAccountContractHistory } from "@portal/hooks/useShowAccountContractHistory";
import { FC, useMemo, useState } from "react";
import { getNestedValue, isEmpty } from "../../../../../utils/form";
import { getPartialFieldPaths } from "../../../../../utils/schema-fields";
import { metadataSchemaForAccountDetail } from "../../historySchemaForAccountDetail";
import { AccountHistoryCard } from "./AccountHistoryCard";
import { HistoryEditModal } from "./HistoryEditModal";

type AccountHistorySectionProps = {
	accountId: SuretyAccountId;
};

export const AccountHistorySection: FC<AccountHistorySectionProps> = ({ accountId }) => {
	const { suretyAccount, suretyAccountIsLoading, suretyAccountError, refetchSuretyAccount } =
		useFetchSuretyAccount(accountId);
	const { metadata, metadataIsLoading, metadataError, refetchAccountMetadata } =
		useFetchAccountMetadata(accountId);
	const showContractHistory = useShowAccountContractHistory(
		suretyAccount?.isContractSuretyAccount ?? true,
	);

	const [isEditModalOpen, setIsEditModalOpen] = useState(false);

	const emptyPartialFields = useMemo(() => {
		if (!suretyAccount?.modelSize || !metadata) return new Set<string>();

		const historySchema = metadataSchemaForAccountDetail(
			suretyAccount.modelSize,
			showContractHistory,
		);
		const partialFields = getPartialFieldPaths(historySchema, metadata);
		const result = new Set<string>();
		for (const path of partialFields) {
			const value = getNestedValue(metadata, path);
			if (isEmpty(value)) {
				result.add(path);
			}
		}
		return result;
	}, [showContractHistory, suretyAccount, metadata]);

	if (suretyAccountIsLoading || metadataIsLoading) {
		return (
			<div className="h-[400px]">
				<CenteredSpinner />
			</div>
		);
	}

	const loadError = suretyAccountError ?? metadataError;
	if (loadError) {
		return (
			<ReportedRetryableLoadError
				error={loadError}
				resource="account history"
				onRetry={suretyAccountError ? refetchSuretyAccount : refetchAccountMetadata}
			/>
		);
	}

	// Reachable when React Query pauses a fetch offline: not loading, no error, no data.
	if (!suretyAccount || !metadata) {
		return (
			<RetryableLoadError
				resource="account history"
				onRetry={() => {
					refetchSuretyAccount();
					refetchAccountMetadata();
				}}
			/>
		);
	}

	const isClosed = suretyAccount.status === "closed";
	const historyPartial = emptyPartialFields.size > 0;

	return (
		<>
			<Card elevation="raised" className="rounded-xl" data-testid="agent-account-history-card">
				<Card.Body className="flex flex-col space-y-5 p-6">
					<div className="flex items-center justify-between w-full">
						<div className="flex items-center gap-x-2">
							<Text type="title" color="primary">
								History
							</Text>
							{historyPartial && (
								<PartialIndicator active={historyPartial} testId="agent-account-history-partial" />
							)}
						</div>
						{!isClosed && suretyAccount.modelSize && (
							<ClickableIcon
								data-testid="agent-account-history-edit"
								type="pencil-square"
								size="mini"
								color="secondary"
								tooltip="Edit"
								aria-label="Edit"
								onClick={() => setIsEditModalOpen(true)}
							/>
						)}
					</div>
					<AccountHistoryCard
						history={metadata}
						emptyPartialFields={emptyPartialFields}
						partialActive={historyPartial}
						showContractHistory={showContractHistory}
					/>
				</Card.Body>
			</Card>

			{isEditModalOpen && suretyAccount.modelSize && (
				<HistoryEditModal
					accountId={accountId}
					modelSize={suretyAccount.modelSize}
					history={metadata}
					updatedAt={suretyAccount.updatedAt}
					showContractHistory={showContractHistory}
					onClose={() => setIsEditModalOpen(false)}
				/>
			)}
		</>
	);
};
